import { z } from 'zod';

import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from './opportunities.js';
import {
  UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES,
  UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE,
} from '../constants/upload-policies.js';
import { CreateSetDateWindowSchema } from '../filters/date-window.js';
import { sanitizePostgresText } from '../validation/postgres-text.js';

/** Max script or brief IDs in one AI Edit job (one LLM rewrite per id). */
export const PERSONA_AI_EDIT_IDS_MAX = 20;

// ─── Persona Opportunity Event Payloads ─────────────────────────────
//
// This file defines the *shape of data* carried on Pub/Sub messages for the
// `persona_opportunities` flow. Pair each schema with a name from
// `PersonaOpportunityEvents` in `constants/event-names.ts`.
//
// Who uses this:
//   - services/api        → validates before publish (optional) / types the publish call
//   - opportunities-engine → validates in the handler via `withErrorHandling(schema, …)`
//
// What belongs here vs elsewhere:
//   - HERE: fields on the wire for each event (Pub/Sub message body)
//   - NOT HERE: HTTP Amber base (`idempotencyKey`) → API route schemas
//   - NOT HERE: DB columns → Prisma models
//
// Add one exported Zod schema (and matching `z.infer` type) per event as the
// flow grows.

const positiveIntKnob = z.number().int().positive();

/** Optional tuning overrides — caller must have `PERSONA_OPPORTUNITY_SETS_QUERY_TUNING` at API gate. */
export const PersonaOpportunityGenerateSetAdvancedSchema = z
  .object({
    candidatePoolSize: positiveIntKnob,
    anglesReturned: positiveIntKnob,
    maxAnglesPerFeature: positiveIntKnob,
    minBrandsPerFeature: positiveIntKnob,
    stickDays: positiveIntKnob,
  })
  .strict();
export type PersonaOpportunityGenerateSetAdvanced = z.infer<
  typeof PersonaOpportunityGenerateSetAdvancedSchema
>;

// ── GENERATE_SET_REQUESTED ───────────────────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_SET_REQUESTED
// When:  user taps Create & Generate (PRD §6).
//
// API validates and publishes this payload. Initial `POST /generate` omits
// `setId` — the worker creates the set row. Retry / regenerate / new_set include
// `setId` so the worker generates into an API-prepared row. `idempotencyKey`
// lives on the envelope.
//
// Keep field shapes aligned with `GeneratePersonaOpportunitySetRequestSchema`
// in services/api/src/schemas/persona-opportunity-sets.schema.ts.
//
// TODO(nexus): requestedBy — Nexus user id for audit / analytics (#AD-2059).
// TODO(nexus): strategic priorities on message vs set row only (#AD-2059).

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_SET_REQUESTED`. */
export const PersonaOpportunityGenerateSetRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    /**
     * Present for retry / overwrite / new_set (API already prepared the row).
     * Absent for first-time `POST /generate` (worker creates the row).
     */
    setId: z.string().min(1).optional(),
    name: z.string().min(1),
    /** Personas to generate for — required ≥1 (PRD §6 Step 2). */
    personaIds: z.array(z.string().min(1)).min(1),
    industry: z.string().min(1),
    industryCompetitors: z.array(z.string().trim().min(1)).min(1),
    /** v1: single ad platform — BQ filters on one @platform until multi-platform SQL lands. */
    adPlatforms: z.array(z.string().trim().min(1)).max(1).optional(),
    advanced: PersonaOpportunityGenerateSetAdvancedSchema.optional(),
  })
  .and(CreateSetDateWindowSchema);

export type PersonaOpportunityGenerateSetRequestedPayload = z.infer<
  typeof PersonaOpportunityGenerateSetRequestedPayload
>;

// ── GENERATE_SCRIPTS_REQUESTED ───────────────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_SCRIPTS_REQUESTED
// When:  user triggers Generate Scripts (bulk bar) or regenerates one script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here.
//
// When `scriptId` is set, the worker regenerates that script only (must belong
// to one of `personaOpportunityIds`). Omit `scriptId` to create more scripts
// on the listed opportunities.

export const PERSONA_SCRIPT_GUIDELINES_MAX_CHARS = 10_000;

/** Max plain-text length for imported script bodies (paste, extract, or Amber payload). */
export const PERSONA_SCRIPT_IMPORT_MAX_CHARS = 100_000;

/** Max length of the optional user-entered title on an imported script or brief. */
export const PERSONA_SCRIPT_IMPORT_TITLE_MAX_CHARS = 200;

/**
 * Optional import title. Blank collapses to `undefined` so the worker falls back to the
 * title the mapper infers from the text.
 */
export const PersonaScriptImportTitleSchema = z
  .string()
  .transform(sanitizePostgresText)
  .pipe(z.string().trim().max(PERSONA_SCRIPT_IMPORT_TITLE_MAX_CHARS))
  .transform((value) => (value.length > 0 ? value : undefined))
  .optional();

/** Max upload size for imported script files (client check and import/extract multipart). */
export const PERSONA_SCRIPT_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

/** Generate POST 4xx when AD-2147's duration probe rejects a reference video. */
export const REFERENCE_VIDEO_TOO_LONG = 'REFERENCE_VIDEO_TOO_LONG' as const;

/** Suggest job FAILED when the scratch reference download exceeds its deadline. */
export const PERSONA_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT =
  'PERSONA_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT' as const;

/** Suggest job FAILED when the downloader rejects the post or returns an error. */
export const PERSONA_REFERENCE_VIDEO_DOWNLOAD_FAILED =
  'PERSONA_REFERENCE_VIDEO_DOWNLOAD_FAILED' as const;

/** Suggest job FAILED when Gemini cannot read the reference (missing or private). */
export const PERSONA_REFERENCE_VIDEO_UNAVAILABLE =
  'PERSONA_REFERENCE_VIDEO_UNAVAILABLE' as const;

/** Suggest job FAILED when reference analysis fails for a reason other than access. */
export const PERSONA_REFERENCE_VIDEO_ANALYSIS_FAILED =
  'PERSONA_REFERENCE_VIDEO_ANALYSIS_FAILED' as const;

export const PersonaOpportunityScriptOutputTypeSchema = z.enum(['script', 'brief', 'both']);
export type PersonaOpportunityScriptOutputType = z.infer<
  typeof PersonaOpportunityScriptOutputTypeSchema
>;

export const PersonaOpportunityScriptsPerAngleSchema = z.number().int().min(1).max(10);

export const PersonaOpportunityVideoReferenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('angle_example') }),
  z.object({ kind: z.literal('upload'), uploadId: z.string().min(1) }),
]);
export type PersonaOpportunityVideoReference = z.infer<
  typeof PersonaOpportunityVideoReferenceSchema
>;

/** Video a persona script was written from. Null column = no reference. */
export const PersonaScriptReferenceVideoSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('upload'), uploadId: z.string().min(1) }),
  z.object({ kind: z.literal('angle_example'), adgeUrl: z.string().url() }),
  z.object({
    kind: z.literal('url'),
    pageUrl: z.string().url(),
    fileUrl: z.string().url().nullable(),
  }),
]);
export type PersonaScriptReferenceVideo = z.infer<typeof PersonaScriptReferenceVideoSchema>;

/** Upload purpose string the generate route requires when `videoReference.kind` is `upload`. */
export const PERSONA_SCRIPT_REFERENCE_PURPOSE = UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE;

/** Upload purpose for a guidelines file persisted by extract (`guidelinesUploadId`). */
export const PERSONA_SCRIPT_GUIDELINES_PURPOSE = UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES;

/**
 * Prompt Manager `usedIn` appended to hub script system prompts.
 * Text only — the host call keeps its own model (`MODEL_NOT_USED`).
 */
export const CREATIVE_SCRIPT_GENERATION_YOUTUBE_USED_IN =
  'creative-script-generation-youtube';

/**
 * Prompt Manager `usedIn` appended to hub brief system prompts.
 * Text only — the host call keeps its own model (`MODEL_NOT_USED`).
 * Brief-shaped: it must not ask for a shooting script.
 */
export const CREATIVE_BRIEF_GENERATION_YOUTUBE_USED_IN =
  'creative-brief-generation-youtube';

/** Wire values for the hub YouTube format control. Off omits the field. */
export const YoutubeFormatSchema = z.enum(['in_stream_16_9', 'shorts_9_16']);
export type YoutubeFormat = z.infer<typeof YoutubeFormatSchema>;

/** English label substituted for `{{youtubeFormat}}` in the YouTube prompt. */
export const YOUTUBE_FORMAT_PROMPT_LABEL: Record<YoutubeFormat, string> = {
  in_stream_16_9: 'In-stream (16:9)',
  shorts_9_16: 'Shorts (9:16)',
};

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_SCRIPTS_REQUESTED`. */
export const PersonaOpportunityGenerateScriptsRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    /** Required for hub/competitor generate; omitted for Post-Flight double down. */
    personaOpportunityIds: z.array(z.string().min(1)).optional(),
    /** Post-Flight double down from the API; creates opportunities in the worker. */
    postFlightDoubleDown: z.lazy(() => PostFlightGenerateScriptsDoubleDownSchema).optional(),
    /**
     * Optional single-script regenerate. When present, the worker asserts the
     * script belongs to one of `personaOpportunityIds` and to `tenantId`.
     */
    scriptId: z.string().min(1).optional(),
    /**
     * Optional guidelines stamped onto newly created scripts in this run.
     * Regenerates keep the existing script row's guidelines unless PATCH'd.
     */
    scriptGuidelines: z.string().trim().min(1).max(PERSONA_SCRIPT_GUIDELINES_MAX_CHARS).optional(),
    /** What the worker should produce. Parsed and forwarded; ignored until AD-2154. */
    outputType: PersonaOpportunityScriptOutputTypeSchema.optional(),
    /** Scripts to create per angle this job when Script is selected. Omit → worker default 5. */
    scriptsPerAngle: PersonaOpportunityScriptsPerAngleSchema.optional(),
    /** Either/or video reference. Parsed and forwarded; ignored until AD-2147. */
    videoReference: PersonaOpportunityVideoReferenceSchema.optional(),
    /** Guidelines file upload id when the modal extracted a PDF/DOCX/TXT. Omit if none. */
    guidelinesUploadId: z.string().min(1).optional(),
    /** Hub YouTube format. Omit when the dialog selection is Off. */
    youtubeFormat: YoutubeFormatSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.postFlightDoubleDown) {
      if (data.personaOpportunityIds && data.personaOpportunityIds.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'postFlightDoubleDown cannot be combined with personaOpportunityIds',
          path: ['personaOpportunityIds'],
        });
      }
      return;
    }
    if (!data.personaOpportunityIds || data.personaOpportunityIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'personaOpportunityIds is required when postFlightDoubleDown is absent',
        path: ['personaOpportunityIds'],
      });
    }
  });

export type PersonaOpportunityGenerateScriptsRequestedPayload = z.output<
  typeof PersonaOpportunityGenerateScriptsRequestedPayload
>;

// ── GENERATE_HOOK_VARIATIONS_REQUESTED ───────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_HOOK_VARIATIONS_REQUESTED
// When:  user clicks + Add 3 hooks on a READY parent persona script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** LLM must return exactly this many hook variants per suggest job. */
export const HOOK_VARIATIONS_PER_PARENT = 3;

/** A persona script holds one primary hook plus at most this many alternates. */
export const PERSONA_SCRIPT_MAX_ALTERNATE_HOOKS = 3;

/** Primary plus alternates. */
export const PERSONA_SCRIPT_MAX_HOOKS = PERSONA_SCRIPT_MAX_ALTERNATE_HOOKS + 1;

/**
 * Highest `slotIndex` hook-variant allocation may use (occupied indexes,
 * including soft-deleted). Bulk fill-up still uses 5 scripts per angle.
 */
export const MAX_PERSONA_SCRIPT_SLOT_INDEX = 20;

/** Prompt Manager `usedIn` for persona-script hook variations. */
export const PERSONA_SCRIPT_HOOK_VARIATIONS_PROMPT_USED_IN =
  'creative-script-hook-variations-persona';

/** Prompt Manager `usedIn` for import structure mapping (not generate). */
export const PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN = 'creative-script-import-persona-structure';

/** Prompt Manager `usedIn` for persona brief AI Edit (Claude text). */
export const PERSONA_BRIEF_EDIT_PROMPT_USED_IN = 'creative-brief-edit-persona-angles';

/**
 * Prompt Manager `usedIn` for Add your script.
 * One prompt writes both a script and a brief. The user message sets OUTPUT
 * and appends REFERENCE VIDEO ANALYSIS only when a reference video was analyzed.
 */
export const ADD_YOUR_SCRIPT_PROMPT_USED_IN = 'add-your-script';
export const DOUBLE_DOWN_CORE_PROMPT_USED_IN = 'creative-script-generation-double-down-core';
export const DOUBLE_DOWN_BRAND_PROMPT_USED_IN = 'creative-script-generation-double-down-brand';

/** Prompt Manager `usedIn` for generate-time reference video analysis (Gemini vision). */
export const PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN =
  'persona-reference-video-analysis';

/**
 * Default Gemini model for that analysis prompt when none is saved.
 * Gemini 3.8 Flash replaces the retired 2.x Flash SKUs and handles long video well.
 */
export const PERSONA_REFERENCE_VIDEO_ANALYSIS_DEFAULT_MODEL = 'gemini-3.8-flash';

/**
 * Slot in the persona script and brief prompts that names the tenant's brand.
 * The brand report describes the advertiser without naming it, and the creative
 * angle is derived from a competitor's ad, so without this slot the only brand
 * named anywhere in the prompt is the competitor's.
 */
export const PERSONA_PROMPT_ADVERTISER_TOKEN = '{advertiser}';

/** Heading of the advertiser block in the persona-angles data sections. */
export const PERSONA_PROMPT_ADVERTISER_HEADING =
  'ADVERTISER — the brand this is for. Name and sell only this brand and its products.';

/**
 * Guardrail under the advertiser slot. The angle, its supporting ad, and the
 * reference video all come from competitors and carry their brand names verbatim.
 */
export const PERSONA_PROMPT_ADVERTISER_GUARDRAIL =
  'Any other brand named anywhere in these inputs — the creative angle, its source ad, the reference video, the recommendations — is a competitor. Use it as context for hook, structure, and pacing only. Never name it, show it, or write for it.';

/** Full block the seeds carry and that fill injects when a saved prompt lacks the slot. */
export const PERSONA_PROMPT_ADVERTISER_BLOCK = `${PERSONA_PROMPT_ADVERTISER_HEADING}
${PERSONA_PROMPT_ADVERTISER_TOKEN}
${PERSONA_PROMPT_ADVERTISER_GUARDRAIL}`;

/** Slot value when neither tenant settings nor the tenant row carry a brand name. */
export const PERSONA_PROMPT_ADVERTISER_UNKNOWN =
  'The brand described under BRAND CONTEXT below (name not available)';

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_HOOK_VARIATIONS_REQUESTED`. */
export const PersonaOpportunityGenerateHookVariationsRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  setId: z.string().min(1),
  personaOpportunityId: z.string().min(1),
  parentScriptId: z.string().min(1),
  requestedBy: z.string().min(1),
});

export type PersonaOpportunityGenerateHookVariationsRequestedPayload = z.infer<
  typeof PersonaOpportunityGenerateHookVariationsRequestedPayload
>;

export const PersonaHookVariationSuggestionSchema = z.object({
  strategyLabel: z.string().trim().min(1),
  hook: z.string().trim().min(1),
  scene1Hearing: z.string().trim().min(1),
  scene1Seeing: z.string().trim().min(1).optional(),
});
export type PersonaHookVariationSuggestion = z.infer<typeof PersonaHookVariationSuggestionSchema>;

/** Correlation status result written by the worker; not persisted until confirm. */
export const PersonaHookVariationsStatusResultSchema = z.object({
  parentScriptId: z.string().min(1),
  variants: z.array(PersonaHookVariationSuggestionSchema).length(HOOK_VARIATIONS_PER_PARENT),
});
export type PersonaHookVariationsStatusResult = z.infer<
  typeof PersonaHookVariationsStatusResultSchema
>;

const HookVariationKeptIndexSchema = z.number().int().min(0).max(HOOK_VARIATIONS_PER_PARENT - 1);

/** HTTP confirm body — `keptIndexes` are unique variant positions 0..2. */
export const PersonaOpportunityConfirmHookVariationsBody = z.object({
  tenantId: z.string().cuid2(),
  idempotencyKey: z.string().min(1),
  correlationId: z.string().min(1),
  parentScriptId: z.string().min(1),
  keptIndexes: z
    .array(HookVariationKeptIndexSchema)
    .max(HOOK_VARIATIONS_PER_PARENT)
    .refine((indexes) => new Set(indexes).size === indexes.length, {
      message: 'keptIndexes must be unique',
    }),
});
export type PersonaOpportunityConfirmHookVariationsBody = z.infer<
  typeof PersonaOpportunityConfirmHookVariationsBody
>;

// ── AI_EDIT_SCRIPTS_REQUESTED ────────────────────────────────────────
// Event: PersonaOpportunityEvents.AI_EDIT_SCRIPTS_REQUESTED
// When:  user submits an AI Edit instruction on one or more persona scripts.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Optional per-cell target — when set, `scriptIds` must contain exactly one id.
 *
 * Mixed-version deploys: older opportunities-engine Zod schemas strip unknown
 * `cell`, then run a whole-script AI edit. Deploy opportunities-engine (this
 * payload + handler) before or together with API/FE that publish `cell`. Do not
 * roll out the API `cell` path alone.
 */
export const PersonaScriptAiEditCellSchema = z.object({
  sceneNumber: z.number().int().nonnegative(),
  field: z.enum(['seeing', 'hearing', 'product', 'props']),
});
export type PersonaScriptAiEditCell = z.infer<typeof PersonaScriptAiEditCellSchema>;

/** Payload for `PERSONA_OPPORTUNITY.AI_EDIT_SCRIPTS_REQUESTED`. */
export const PersonaOpportunityAiEditScriptsRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().min(1),
    setId: z.string().min(1),
    scriptIds: z.array(z.string().min(1)).min(1).max(PERSONA_AI_EDIT_IDS_MAX),
    instruction: z.string().min(1).max(AI_EDIT_INSTRUCTION_MAX_LENGTH),
    cell: PersonaScriptAiEditCellSchema.optional(),
  })
  .refine((data) => data.cell === undefined || data.scriptIds.length === 1, {
    message: 'cell requires exactly one scriptId',
    path: ['scriptIds'],
  });
export type PersonaOpportunityAiEditScriptsRequestedPayload = z.infer<
  typeof PersonaOpportunityAiEditScriptsRequestedPayload
>;

// ── AI_EDIT_BRIEFS_REQUESTED ─────────────────────────────────────────
// Event: PersonaOpportunityEvents.AI_EDIT_BRIEFS_REQUESTED
// When:  user submits an AI Edit instruction on one or more persona briefs.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Optional per-cell target — never `videoName` / title.
 *
 * Mixed-version deploys: older opportunities-engine Zod schemas strip unknown
 * `cell`, then run a whole-brief AI edit. Deploy opportunities-engine (this
 * payload + handler) before or together with API/FE that publish `cell`. Do not
 * roll out the API `cell` path alone.
 */
export const PersonaBriefAiEditCellSchema = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('hook'),
    hookIndex: z.number().int().min(1).max(4),
    field: z.enum(['direction', 'line']),
  }),
  z.object({
    target: z.literal('concept'),
    field: z.enum(['direction', 'line']),
  }),
  z.object({
    target: z.literal('cta'),
    field: z.enum(['direction', 'line']),
  }),
  z.object({ target: z.literal('notes') }),
  z.object({ target: z.literal('preferences') }),
]);
export type PersonaBriefAiEditCell = z.infer<typeof PersonaBriefAiEditCellSchema>;

/** Payload for `PERSONA_OPPORTUNITY.AI_EDIT_BRIEFS_REQUESTED`. */
export const PersonaOpportunityAiEditBriefsRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().min(1),
    setId: z.string().min(1),
    briefIds: z.array(z.string().min(1)).min(1).max(PERSONA_AI_EDIT_IDS_MAX),
    instruction: z.string().min(1).max(AI_EDIT_INSTRUCTION_MAX_LENGTH),
    cell: PersonaBriefAiEditCellSchema.optional(),
  })
  .refine((data) => data.cell === undefined || data.briefIds.length === 1, {
    message: 'cell requires exactly one briefId',
    path: ['briefIds'],
  });
export type PersonaOpportunityAiEditBriefsRequestedPayload = z.infer<
  typeof PersonaOpportunityAiEditBriefsRequestedPayload
>;

// ── STORYBOARD_GENERATION_REQUESTED ──────────────────────────────────
// Event: PersonaOpportunityEvents.STORYBOARD_GENERATION_REQUESTED
// When:  user clicks Generate storyboard on a READY persona script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Payload for `PERSONA_OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED`. */
export const PersonaOpportunityStoryboardGenerationRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  setId: z.string().min(1),
  scriptId: z.string().min(1),
  /** Sketch sheet or photoreal animatic — same variants as legacy opportunity scripts. */
  variant: z.enum(['sketch', 'panels']).default('sketch'),
});
export type PersonaOpportunityStoryboardGenerationRequestedPayload = z.infer<
  typeof PersonaOpportunityStoryboardGenerationRequestedPayload
>;

// ── SUGGEST_SCRIPTS_REQUESTED ────────────────────────────────────────
// Event: PersonaOpportunityEvents.SUGGEST_SCRIPTS_REQUESTED
// When:  user submits Add your script on the persona Scripts tab.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Worker finds-or-creates the set's
// synthetic custom angle, then generates N scripts onto it.

export const PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS = 10_000;
export const PERSONA_SUGGEST_SCRIPT_VARIATIONS_MAX = 5;
export const PERSONA_SUGGEST_SCRIPT_REFERENCE_URLS_MAX = 3;

const SCRATCH_REFERENCE_VIDEO_HOSTS = ['instagram.com', 'tiktok.com', 'youtube.com', 'youtu.be'] as const;

function hostMatches(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

function isYouTubeVideoPath(url: URL): boolean {
  if (url.pathname === '/redirect' || url.pathname.startsWith('/redirect/')) return false;
  if (url.pathname === '/watch' || url.pathname === '/watch/') {
    return (url.searchParams.get('v')?.trim().length ?? 0) > 0;
  }
  const shorts = /^\/shorts\/([^/]+)/.exec(url.pathname);
  if (shorts != null && (shorts[1]?.length ?? 0) > 0) return true;
  const live = /^\/live\/([^/]+)/.exec(url.pathname);
  return live != null && (live[1]?.length ?? 0) > 0;
}

function isYouTubeSharePath(url: URL): boolean {
  const id = url.pathname.replace(/^\//, '').split('/')[0] ?? '';
  return /^[\w-]{6,}$/.test(id);
}

function isInstagramVideoPath(url: URL): boolean {
  return (
    /^\/(?:reel|reels|p)\/[^/]+/.test(url.pathname) ||
    /^\/[^/]+\/reel\/[^/]+/.test(url.pathname)
  );
}

function isTikTokVideoPath(url: URL): boolean {
  if (/\/video\/[^/]+/.test(url.pathname) || /^\/t\/[^/]+/.test(url.pathname)) return true;
  const host = url.hostname.toLowerCase().replace(/\.$/, '');
  if (host !== 'vm.tiktok.com') return false;
  const id = url.pathname.replace(/^\//, '').split('/')[0] ?? '';
  return id.length > 0;
}

/** One https Instagram, TikTok, or YouTube video URL. Rejects extra links, http, and non-video paths. */
export function isScratchReferenceVideoUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed) || trimmed.includes(',')) return false;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (url.username.length > 0 || url.password.length > 0 || url.port.length > 0) return false;
  const host = url.hostname.toLowerCase().replace(/\.$/, '');
  if (hostMatches(host, 'youtu.be')) return isYouTubeSharePath(url);
  if (hostMatches(host, 'youtube.com')) return isYouTubeVideoPath(url);
  if (hostMatches(host, 'instagram.com')) return isInstagramVideoPath(url);
  if (hostMatches(host, 'tiktok.com')) return isTikTokVideoPath(url);
  return SCRATCH_REFERENCE_VIDEO_HOSTS.some((suffix) => hostMatches(host, suffix));
}

/** One https Instagram, TikTok, or YouTube video URL, including youtu.be. */
export const ScratchReferenceVideoUrlSchema = z
  .string()
  .trim()
  .refine(
    isScratchReferenceVideoUrl,
    'Paste one public video link (YouTube Shorts or watch, TikTok video, Instagram reel)',
  );

/** Payload for `PERSONA_OPPORTUNITY.SUGGEST_SCRIPTS_REQUESTED`. */
export const PersonaOpportunitySuggestOutputTypeSchema = z.enum(['script', 'brief']);
export type PersonaOpportunitySuggestOutputType = z.infer<
  typeof PersonaOpportunitySuggestOutputTypeSchema
>;

export const POST_FLIGHT_DOUBLE_DOWN_BLOCK_MAX = 8;
export const POST_FLIGHT_DOUBLE_DOWN_TASK_TYPE = 'post-flight-double-down';
/** Stage id on the global task card. Subtask ids match the script phases. */
export const POST_FLIGHT_DOUBLE_DOWN_TASK_STAGE_ID = 'generate';

/** One block on the Double down job. `count` is how many scripts that block writes. */
export const PostFlightDoubleDownJobBlockSchema = z.object({
  instruction: z.string().trim().min(1).max(4_000),
  count: z.number().int().min(1).max(PERSONA_SUGGEST_SCRIPT_VARIATIONS_MAX),
  leverId: z.string().trim().min(1).max(80).nullable(),
});

export const PostFlightDoubleDownBlockSchema = PostFlightDoubleDownJobBlockSchema.extend({
  title: z.string().trim().min(1).max(200),
  why: z.string().trim().max(4_000).default(''),
});

export const PostFlightDoubleDownParentSchema = z.object({
  hash: z.string().trim().min(1),
  title: z.string().trim().min(1),
  videoUrl: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  source: z.string(),
  windowDays: z.number().int(),
  asOf: z.string().nullable().optional(),
  firstSeen: z.string().nullable().optional(),
  windowStart: z.string().nullable().optional(),
  windowEnd: z.string().nullable().optional(),
  daysLive: z.number().nullable().optional(),
  clientName: z.string().trim().min(1),
  product: z.string().nullable(),
  concept: z.string().nullable(),
  persona: z.string().nullable(),
  motivation: z.string().nullable(),
  painPoint: z.string().nullable(),
  presenter: z.string().nullable(),
  hookType: z.string().nullable(),
  cpa: z.number().nullable(),
  accountCpa: z.number().nullable(),
  pctBetter: z.number().nullable().optional(),
  purchases: z.number().nullable().optional(),
  spend: z.number().nullable().optional(),
  adRank: z.number().nullable().optional(),
  cpaFirstHalf: z.number().nullable().optional(),
  cpaSecondHalf: z.number().nullable().optional(),
  thumbstop: z.number().nullable(),
  holdRate: z.number().nullable(),
  hookScore: z.number().nullable(),
  holdScore: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  transcript: z.string().nullable(),
});

export const PostFlightDoubleDownSwitchRowSchema = z
  .object({
    section: z.string(),
    window_days: z.number().nullable().optional(),
    source: z.string().nullable().optional(),
    rank: z.number().nullable().optional(),
    item: z.string().nullable().optional(),
    flag: z.string().nullable().optional(),
    concept: z.string().nullable().optional(),
    product: z.string().nullable().optional(),
    best_line: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    hook_score: z.number().nullable().optional(),
    hold_score: z.number().nullable().optional(),
    hash: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    cpa: z.number().nullable().optional(),
    account_cpa: z.number().nullable().optional(),
    spend_share: z.number().nullable().optional(),
    ad_count: z.number().nullable().optional(),
  })
  .passthrough();

export const PostFlightDoubleDownSchema = z.object({
  parent: PostFlightDoubleDownParentSchema,
  blocks: z.array(PostFlightDoubleDownBlockSchema).min(1).max(POST_FLIGHT_DOUBLE_DOWN_BLOCK_MAX),
  switchTo: z.array(PostFlightDoubleDownSwitchRowSchema),
});
export type PostFlightDoubleDown = z.infer<typeof PostFlightDoubleDownSchema>;

/**
 * Redis claim metadata. The body fingerprint is a stored value on `claim`
 * (same-key mismatch), not a second cache key.
 */
export const PostFlightDoubleDownIdempotencyClaimSchema = z.object({
  idemKey: z.string().min(1),
  claim: z.object({
    correlationId: z.string().min(1),
    ownerId: z.string().min(1),
    fingerprint: z.string().min(1),
    state: z.enum(['PENDING', 'ACCEPTED']).optional(),
  }),
});
export type PostFlightDoubleDownIdempotencyClaim = z.infer<
  typeof PostFlightDoubleDownIdempotencyClaimSchema
>;

/** Post-Flight double down job nested under GENERATE_SCRIPTS_REQUESTED. */
export const PostFlightGenerateScriptsDoubleDownSchema = z.object({
  setId: z.string().min(1),
  requestedBy: z.string().min(1),
  /** Hub card index key. The worker creates the card; the route does not. */
  userEmail: z.string().email().optional(),
  /**
   * Tenant settings partition at publish time. The standing Post-Flight set is
   * shared across partitions, so generation uses this value. Older in-flight
   * messages omit it and the worker falls back to the set.
   */
  customerPartition: z.string().max(255).nullable().optional(),
  doubleDown: PostFlightDoubleDownSchema,
  /** API attaches the idempotency-key claim; worker extends TTL on start and releases it on failure. */
  idempotencyClaim: PostFlightDoubleDownIdempotencyClaimSchema.optional(),
});

export const PersonaOpportunitySuggestScriptsRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  description: z.string().trim().min(1).max(PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS),
  variations: z.number().int().min(1).max(PERSONA_SUGGEST_SCRIPT_VARIATIONS_MAX),
  referenceVideoUrl: ScratchReferenceVideoUrlSchema.optional(),
  outputType: PersonaOpportunitySuggestOutputTypeSchema.default('script'),
  /** Hub YouTube format. Omit when the dialog selection is Off. */
  youtubeFormat: YoutubeFormatSchema.optional(),
});

export type PersonaOpportunitySuggestScriptsRequestedPayload = z.infer<
  typeof PersonaOpportunitySuggestScriptsRequestedPayload
>;

// ── IMPORT_SCRIPT_FROM_TEXT_REQUESTED ────────────────────────────────
// Event: PersonaOpportunityEvents.IMPORT_SCRIPT_FROM_TEXT_REQUESTED
// When:  user confirms Import on the persona Scripts tab.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Worker maps structure via LLM, then
// persists one script whose Full Script is `text`.

/** Payload for `PERSONA_OPPORTUNITY.IMPORT_SCRIPT_FROM_TEXT_REQUESTED`. */
export const PersonaOpportunityImportScriptFromTextRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  text: z
    .string()
    .transform(sanitizePostgresText)
    .pipe(z.string().trim().min(1).max(PERSONA_SCRIPT_IMPORT_MAX_CHARS)),
  title: PersonaScriptImportTitleSchema,
});
export type PersonaOpportunityImportScriptFromTextRequestedPayload = z.infer<
  typeof PersonaOpportunityImportScriptFromTextRequestedPayload
>;

// ── IMPORT_BRIEF_FROM_TEXT_REQUESTED ─────────────────────────────────
// Event: PersonaOpportunityEvents.IMPORT_BRIEF_FROM_TEXT_REQUESTED
// When:  user confirms Import brief (paste or extracted file text).
//
// Worker maps the text into one PersonaBrief on the set's custom angle.

/** Payload for `PERSONA_OPPORTUNITY.IMPORT_BRIEF_FROM_TEXT_REQUESTED`. */
export const PersonaOpportunityImportBriefFromTextRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  text: z
    .string()
    .transform(sanitizePostgresText)
    .pipe(z.string().trim().min(1).max(PERSONA_SCRIPT_IMPORT_MAX_CHARS)),
  title: PersonaScriptImportTitleSchema,
});
export type PersonaOpportunityImportBriefFromTextRequestedPayload = z.infer<
  typeof PersonaOpportunityImportBriefFromTextRequestedPayload
>;

// ── RESOLVE_REFERENCE_LINKS_REQUESTED ────────────────────────────────
// Event: PersonaOpportunityEvents.RESOLVE_REFERENCE_LINKS_REQUESTED
// When:  Suggest Script modal pre-fetches pasted URLs before generation.

/** Payload for `PERSONA_OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED`. */
export const PersonaOpportunityResolveReferenceLinksRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  referenceUrls: z.array(z.string().url()).max(PERSONA_SUGGEST_SCRIPT_REFERENCE_URLS_MAX).default([]),
});
export type PersonaOpportunityResolveReferenceLinksRequestedPayload = z.infer<
  typeof PersonaOpportunityResolveReferenceLinksRequestedPayload
>;
